import { Howl, Howler } from 'howler';

export interface AudioConfig {
  duration: number;
  sampleRate: number;
  muted: boolean;
  loop: boolean;
  volume: number;
  offsetStart: number;
  offsetEnd: number;
}

export type AudioPlaybackState = 'idle' | 'starting' | 'playing' | 'waiting' | 'paused' | 'error';

Howler.autoUnlock = true;
// 旁白由引用计数池限制在当前预加载窗口；优先保持共享 AudioContext 运行，
// CDN 不允许 Web Audio XHR 时再由单个 Howl 降级到 HTML5 Audio。
Howler.autoSuspend = false;

/** Howler 播放适配器：公开接口保持与原 HTMLAudioElement 实现一致。 */
export class Audio {
  private static _masterVolume = 1;
  private static _masterMuted = false;
  private static readonly instances = new Set<Audio>();

  public static setMasterVolume(volume: number): void {
    Audio._masterVolume = Math.max(0, Math.min(1, volume));
    Audio.instances.forEach((audio) => audio.applyVolume());
  }

  public static setMasterMuted(muted: boolean): void {
    Audio._masterMuted = muted;
    Audio.instances.forEach((audio) => audio.applyVolume());
  }

  private readonly config: AudioConfig = {
    duration: 2,
    sampleRate: 0,
    muted: false,
    loop: false,
    volume: 1,
    offsetStart: -1,
    offsetEnd: -1
  };

  private howl: Howl | null = null;
  private soundId: number | null = null;
  private objectUrl: string | null = null;
  private audioDuration = 0;
  private _currentTime = 0;
  private _state: AudioPlaybackState = 'idle';
  private playbackGain = 1;
  private completed = false;
  private buffering = false;
  private playbackBlocked = false;
  private requestToken = 0;
  private loadToken = 0;
  private pendingLoadResolve: ((loaded: boolean) => void) | null = null;
  private endTimer: ReturnType<typeof setTimeout> | null = null;

  private playbackBlockedHandler: (() => void) | null = null;
  private playbackStartedHandler: (() => void) | null = null;
  private playbackEndedHandler: (() => void) | null = null;
  private playbackBufferingHandler: ((buffering: boolean) => void) | null = null;
  private playbackErrorHandler: ((error: unknown) => void) | null = null;

  constructor() {
    Audio.instances.add(this);
  }

  public load(resource: File | string, preferHtml5 = false): Promise<boolean> {
    this.releaseHowl();
    const token = ++this.loadToken;
    const source =
      resource instanceof File
        ? URL.createObjectURL(resource)
        : `${resource}${resource.includes('?') ? '&' : '?'}_t=${Date.now()}`;

    if (resource instanceof File) this.objectUrl = source;

    return new Promise<boolean>((resolve) => {
      this.pendingLoadResolve = resolve;
      let fallbackAttempted = false;

      const finish = (loaded: boolean) => {
        if (token !== this.loadToken) return;
        this.pendingLoadResolve = null;
        resolve(loaded);
      };

      const createHowl = (html5: boolean): Howl => {
        const howl = new Howl({
          src: [source],
          html5,
          preload: false,
          pool: 1,
          loop: this.config.loop,
          mute: this.config.muted,
          volume: this.getActualVolume(),
          onload: () => {
            if (token !== this.loadToken || this.howl !== howl) return;

            this.audioDuration = howl.duration();
            this.normalizeOffsetConfig();
            this._state = 'idle';
            finish(true);
          },
          onloaderror: (_soundId, error) => {
            if (token !== this.loadToken || this.howl !== howl) return;

            if (!html5 && !fallbackAttempted) {
              fallbackAttempted = true;
              howl.off();
              howl.unload();

              const fallbackHowl = createHowl(true);
              this.howl = fallbackHowl;
              fallbackHowl.load();
              return;
            }

            this._state = 'error';
            this.playbackErrorHandler?.(error);
            finish(false);
          },
          onplay: this.handlePlay,
          onplayerror: this.handlePlayError,
          onend: this.handleEnded,
          onpause: this.handlePause,
          onstop: this.handleStop
        });

        return howl;
      };

      const howl = createHowl(preferHtml5);
      this.howl = howl;
      howl.load();
    });
  }

  public seek(time: number): void {
    this._currentTime = this.formatTime(time);
  }

  public seekPlay(time: number): void {
    this.playAt(time);
  }

  public playAt(time: number): void {
    this.seek(time);
    if (this.handled || !this.howl) return;
    if (!this.loop && this._currentTime >= this.duration) return;

    const howl = this.howl;
    const token = ++this.requestToken;
    this.completed = false;
    this.playbackBlocked = false;
    this._state = 'starting';
    this.setBuffering(true);
    this.applyVolume();

    // Howler 的 seek(id) 在播放中会执行 pause -> seek -> play。
    // iOS WebKit 对紧邻的 play/pause 很敏感，因此先设置起点，再一次性起播。
    howl.seek(Math.max(0, this.config.offsetStart) + this._currentTime);
    const soundId = howl.play();
    this.soundId = soundId;

    // token 用于取消 unlock 后的过期重试。
    if (token !== this.requestToken) howl.stop(soundId);
  }

  public pause(): void {
    const howl = this.howl;
    const soundId = this.soundId;
    if (howl && soundId !== null) {
      const position = howl.seek(soundId);
      if (Number.isFinite(position)) {
        this._currentTime = this.formatTime(position - Math.max(0, this.config.offsetStart));
      }
      howl.pause(soundId);
    }

    this.invalidateRequest();
    this.clearEndTimer();
    this.playbackBlocked = false;
    this.setBuffering(false);
    if (this._state !== 'idle' && this._state !== 'error') this._state = 'paused';
  }

  public stop(): void {
    const howl = this.howl;
    const soundId = this.soundId;
    this.invalidateRequest();
    this.clearEndTimer();
    if (howl && soundId !== null) howl.stop(soundId);
    this.soundId = null;
    this._currentTime = 0;
    this.completed = false;
    this.playbackBlocked = false;
    if (this._state !== 'error') this._state = 'idle';
    this.setBuffering(false);
  }

  private readonly handlePlay = (soundId: number) => {
    if (soundId !== this.soundId) return;
    this.playbackBlocked = false;
    this._state = 'playing';
    this.setBuffering(false);
    this.scheduleEndTimer();
    this.playbackStartedHandler?.();
  };

  private readonly handlePlayError = (soundId: number, error: unknown) => {
    if (soundId !== this.soundId || !this.howl) return;

    const token = this.requestToken;
    const howl = this.howl;
    this._state = 'waiting';
    this.playbackBlocked = true;
    this.setBuffering(false);
    this.playbackBlockedHandler?.();
    this.playbackErrorHandler?.(error);

    howl.once('unlock', () => {
      if (token !== this.requestToken || this.howl !== howl) return;
      this.soundId = null;
      this.playbackBlocked = false;
      this._state = 'idle';
      this.playAt(this._currentTime);
    });
  };

  private readonly handleEnded = (soundId: number) => {
    if (soundId !== this.soundId || this.completed) return;

    this.clearEndTimer();
    if (this.loop) {
      this._currentTime = 0;
      this._state = 'playing';
      return;
    }

    this.invalidateRequest();
    this.soundId = null;
    this._currentTime = this.duration;
    this._state = 'idle';
    this.completed = true;
    this.setBuffering(false);
    if (!this.loop) this.playbackEndedHandler?.();
  };

  private readonly handlePause = (soundId: number) => {
    if (soundId !== this.soundId) return;
    this.clearEndTimer();
    if (this._state !== 'error') this._state = 'paused';
    this.setBuffering(false);
  };

  private readonly handleStop = (soundId: number) => {
    if (soundId !== this.soundId) return;
    this.clearEndTimer();
    if (this._state !== 'error') this._state = 'idle';
    this.setBuffering(false);
  };

  private scheduleEndTimer(): void {
    this.clearEndTimer();
    if (this.loop) return;

    const remaining = this.duration - this._currentTime;
    if (remaining <= 0) return;

    this.endTimer = setTimeout(() => {
      const soundId = this.soundId;
      if (soundId === null) return;
      this.howl?.stop(soundId);
      this.handleEnded(soundId);
    }, remaining * 1000);
  }

  private clearEndTimer(): void {
    if (this.endTimer === null) return;
    clearTimeout(this.endTimer);
    this.endTimer = null;
  }

  private invalidateRequest(): void {
    this.requestToken += 1;
  }

  private setBuffering(buffering: boolean): void {
    if (this.buffering === buffering) return;
    this.buffering = buffering;
    this.playbackBufferingHandler?.(buffering);
  }

  private formatTime(time: number): number {
    if (this.loop && this.duration > 0) time %= this.duration;
    else time = Math.min(time, this.duration);
    return Math.max(0, time);
  }

  public async updateOffsetTime(start = -1, end = -1): Promise<void> {
    this.config.offsetStart = start;
    this.config.offsetEnd = end;
    this.normalizeOffsetConfig();
    this.seek(0);
  }

  private normalizeOffsetConfig(): void {
    const normalizedStart = Math.max(
      0,
      Math.min(this.config.offsetStart < 0 ? 0 : this.config.offsetStart, this.audioDuration)
    );
    let normalizedEnd = Math.max(
      0,
      Math.min(
        this.config.offsetEnd < 0 ? this.audioDuration : this.config.offsetEnd,
        this.audioDuration
      )
    );
    if (normalizedEnd < normalizedStart) normalizedEnd = this.audioDuration;

    this.config.offsetStart = normalizedStart;
    this.config.offsetEnd = normalizedEnd;
    this.config.duration = normalizedEnd - normalizedStart;
  }

  private getActualVolume(): number {
    const own = this.config.muted ? 0 : this.config.volume;
    const master = Audio._masterMuted ? 0 : Audio._masterVolume;
    return Math.max(0, Math.min(1, (own * master * this.playbackGain) ** 2));
  }

  private applyVolume(): void {
    if (!this.howl) return;
    this.howl.volume(this.getActualVolume());
    this.howl.mute(this.config.muted || Audio._masterMuted);
  }

  public destroy(): void {
    Audio.instances.delete(this);
    this.playbackBlockedHandler = null;
    this.playbackStartedHandler = null;
    this.playbackEndedHandler = null;
    this.playbackBufferingHandler = null;
    this.playbackErrorHandler = null;
    this.releaseHowl();
    this._state = 'idle';
  }

  private releaseHowl(): void {
    this.loadToken += 1;
    this.invalidateRequest();
    this.clearEndTimer();
    this.pendingLoadResolve?.(false);
    this.pendingLoadResolve = null;

    if (this.howl) {
      this.howl.off();
      this.howl.stop();
      this.howl.unload();
      this.howl = null;
    }

    if (this.objectUrl) {
      URL.revokeObjectURL(this.objectUrl);
      this.objectUrl = null;
    }

    this.soundId = null;
    this.audioDuration = 0;
    this._currentTime = 0;
    this.completed = false;
    this.playbackBlocked = false;
    this.setBuffering(false);
  }

  /** Howler 会通过全局 autoUnlock 处理首次真实用户手势。 */
  public prepareForAutoplay(): void {}

  /** 允许被 playerror 阻止的请求在当前真实手势内重新发起。 */
  public prepareForUserGesture(): void {
    if (this._state === 'error' || this.playbackBlocked) {
      this.playbackBlocked = false;
      this._state = 'idle';
    }
  }

  public setPlaybackGain(value: number): void {
    this.playbackGain = Math.max(0, Math.min(1, value));
    this.applyVolume();
  }

  public setPlaybackBlockedHandler(handler: (() => void) | null): void {
    this.playbackBlockedHandler = handler;
  }

  public setPlaybackStartedHandler(handler: (() => void) | null): void {
    this.playbackStartedHandler = handler;
  }

  public setPlaybackEndedHandler(handler: (() => void) | null): void {
    this.playbackEndedHandler = handler;
  }

  public setPlaybackBufferingHandler(handler: ((buffering: boolean) => void) | null): void {
    this.playbackBufferingHandler = handler;
  }

  public setPlaybackErrorHandler(handler: ((error: unknown) => void) | null): void {
    this.playbackErrorHandler = handler;
  }

  public set loop(value: boolean) {
    this.config.loop = value;
    if (!this.howl) return;
    if (this.soundId === null) this.howl.loop(value);
    else this.howl.loop(value, this.soundId);
  }

  public get loop(): boolean {
    return this.config.loop;
  }

  public set volume(value: number) {
    this.config.volume = value;
    this.applyVolume();
  }

  public get volume(): number {
    return this.config.volume;
  }

  public set muted(value: boolean) {
    this.config.muted = value;
    this.applyVolume();
  }

  public get muted(): boolean {
    return this.config.muted;
  }

  public get duration(): number {
    return this.config.duration;
  }

  public get currentTime(): number {
    if (this.howl && this.soundId !== null && this.howl.playing(this.soundId)) {
      const position = this.howl.seek(this.soundId);
      if (Number.isFinite(position)) {
        return this.formatTime(position - Math.max(0, this.config.offsetStart));
      }
    }
    return this._currentTime;
  }

  public get state(): AudioPlaybackState {
    return this._state;
  }

  public get handled(): boolean {
    return (
      this.completed ||
      this._state === 'starting' ||
      this._state === 'playing' ||
      this._state === 'waiting' ||
      this._state === 'error'
    );
  }

  public get playing(): boolean {
    return this._state === 'starting' || this._state === 'playing' || this._state === 'waiting';
  }
}
